import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import SubcomponentType from './subcomponents/types'
import PropertyGroup from '../properties/PropertyGroup';
import StringProperty from '../properties/StringProperty';
import NumberProperty from '../properties/NumberProperty';
import BoolProperty from '../properties/BoolProperty';
import HeaderProperty from '../properties/HeaderProperty';
import ColorProperty from '../properties/ColorProperty';
import SliderProperty from '../properties/SliderProperty';
import DropdownProperty from '../properties/DropdownProperty';
import AssetProperty from '../properties/AssetProperty';
import CameraProperty from '../properties/CameraProperty';
import StepProperty from '../properties/StepProperty';
import { StoreActions } from '../../state/store';
import TextProperty from '../properties/TextProperty';
import UIEditorValueChangeAction from '../../actions/ui/UIEditorValueChangeAction';
import ColorAlphaProperty from '../properties/ColorAlphaProperty.tsx';
import IconSelectionProperty from '../properties/IconSelectionProperty';
import { AlignTextLeftIcon, AlignTextCenterIcon, AlignTextRightIcon, AnchorTopIcon, AnchorMiddleIcon, AnchorBottomIcon } from '../Iconset';

function propertiesEqual(left, right)
{
	let equal = true;
	for (const key of Object.keys(left))
	{
		if (!shallowEqual(left[key], right[key]))
		{
			equal = false;
		}
	}
	return equal; // the properties are not equal, we need to update (rerender)
}

const PropertyTypes = Object.freeze({
	Group: 'group',
	Text: 'text',
	TextArea: 'textarea',
	Numeric: 'numeric',
	Toggle: 'toggle',
	Image: 'image',
	Video: 'video',
	Audio: 'audio',
	Color: 'color',
	Range: 'range',
	Step: 'step',
	Url: 'url',
	Camera: 'camera',
	Anchor: 'anchor',
	InsetAnchor: 'inset-anchor',
	Align: 'align',
	TextAreaPreset: 'text-area-preset',
	OptionLayout: 'option-layout',
	Size3: 'size3',
	FontSize: 'font-size',
	InsetType: 'inset-type',
});

export default function UIEditorSubcomponent(props)
{
	const dispatch = useDispatch();

	const storedProperties = useSelector(state => props.slide ? state.uiModule.slideMaster : state.uiModule.ui, (left, right) =>
	{
		if (left[props.slide ?? props.step] && left[props.slide ?? props.step][props.template] && left[props.slide ?? props.step][props.template][props.subcomponent] &&
			right[props.slide ?? props.step] && right[props.slide ?? props.step][props.template] && right[props.slide ?? props.step][props.template][props.subcomponent])
		{
			const leftSubcomponent = left[props.slide ?? props.step][props.template][props.subcomponent];
			const rightSubcomponent = right[props.slide ?? props.step][props.template][props.subcomponent];
			return propertiesEqual(leftSubcomponent, rightSubcomponent);
		}

		return false;
	});

	const activeWindow = useSelector(state => state.workspaceModule.activeWindow);

	const [previousValue, setPreviousValue] = useState(null);

	const getProperty = (key) =>
	{
		if (storedProperties[props.slide ?? props.step] &&
			storedProperties[props.slide ?? props.step][props.template] &&
			storedProperties[props.slide ?? props.step][props.template][props.subcomponent])
		{
			return storedProperties[props.slide ?? props.step][props.template][props.subcomponent][key];
		}

		return null;
	}

	const getKeyExists = (key) =>
	{
		return getProperty(key) !== null && getProperty !== undefined;
	}

	const getPropertyValue = (key) =>
	{
		const property = getProperty(key);
		if (property)
		{
			return property.value;
		}
		return null;
	}

	const getPropertyChanged = (key) =>
	{
		const property = getProperty(key);
		if (property && property.changed && property.changed === true)
		{
			return true;
		}
		return false;
	}

	const getPropertyDefaultValue = (key) =>
	{
		const property = getProperty(key);
		if (property)
		{
			return property.default;
		}
		return null;
	}

	useEffect(() =>
	{
		if (props.properties)
		{
			for (let key of Object.keys(props.properties))
			{
				if (!getKeyExists(key))
				{
					dispatch(StoreActions.ui.setProperty({
						step: props.step,
						slide: props.slide,
						template: props.template,
						subcomponent: props.subcomponent,
						property: {
							name: key,
							type: props.properties[key].type,
							value: props.properties[key].default !== undefined ? props.properties[key].default : '',
							default: props.properties[key].default !== undefined ? props.properties[key].default : null,
						}
					}))
				}
			}
		}
	}, []) // create the data the first time this object appears

	function onValueChanged(key, value, changed = true)
	{
		if(previousValue === null)
		{
			setPreviousValue(getPropertyValue(key))
		}

		// console.log("UI property " + key + " [" + props.properties[key].type + "] changed: " + value);

		const templateData = {
			step: props.step,
			slide: props.slide,
			breakSync: props.slide ? false : true,
			template: props.template,
			subcomponent: props.subcomponent,
			property: {
				name: key,
				type: props.properties[key].type,
				value: value,
				changed: !props.slide ? changed : false,
			}
		}

		dispatch(StoreActions.ui.setProperty(templateData));
	}

	function onValueCommitted(key, value, changed = true)
	{
		//console.log("UI property " + key + " [" + props.properties[key].type + "] commited: " + value);

		const templateData = {
			step: props.step,
			slide: props.slide,
			breakSync: props.slide ? false : true,
			template: props.template,
			subcomponent: props.subcomponent,
			property: {
				name: key,
				type: props.properties[key].type,
				value: value,
				changed: !props.slide ? changed : false,
			}
		}

		new UIEditorValueChangeAction({ ...templateData }, previousValue !== null ? previousValue : getPropertyValue(key));
		setPreviousValue(null);
	}

	function findAlpha(visibleProperties, colorKey)
	{
		for (let key of Object.keys(visibleProperties))
		{
			if(key.endsWith('alpha'))
			{
				let keyNoAlpha = key.replace('alpha', '');
				if(colorKey.startsWith(keyNoAlpha))
				{
					return { ...visibleProperties[key], key: key };
				}
			}
		}
		return null;
	}

	let properties = [];

	let visibleProperties = props.properties;
	//#region Unique INSET logic
	if (props.subcomponent === SubcomponentType.INSETS)
	{
		visibleProperties = {};
		for (let key of Object.keys(props.properties))
		{
			if (key.endsWith('type'))
			{ // iterate on key types only
				visibleProperties[key] = props.properties[key]; // add type
				const insetIdx = key.substring(0, 7);
				let currentInsetProperties = Object.fromEntries(Object.entries(props.properties).filter(([k, v]) => k.startsWith(insetIdx)));
				const insetType = getPropertyValue(key);
				switch (insetType)
				{
					case 'image':
						for (let key of Object.keys(currentInsetProperties))
						{
							if (key.endsWith('image') || key.endsWith('height') || key.endsWith('anchor'))
							{
								visibleProperties[key] = currentInsetProperties[key];
							}
						}
						break;
					case 'video':
						for (let key of Object.keys(currentInsetProperties))
						{
							if (key.endsWith('image') || key.endsWith('video') || key.endsWith('height') || key.endsWith('anchor'))
							{
								visibleProperties[key] = currentInsetProperties[key];
							}
						}
						break;
					case 'audio':
						for (let key of Object.keys(currentInsetProperties))
						{
							if (key.endsWith('image') || key.endsWith('audio') || key.endsWith('height') || key.endsWith('anchor'))
							{
								visibleProperties[key] = currentInsetProperties[key];
							}
						}
						break;
					case 'camera':
						for (let key of Object.keys(currentInsetProperties))
						{
						//exclude camera from slide master editor
							if (key.endsWith('title') || (key.endsWith('camera') && !props.slide) || key.endsWith('height') || key.endsWith('anchor'))
							{
								visibleProperties[key] = currentInsetProperties[key];
							}
						}
						break;
					case 'url':
						for (let key of Object.keys(currentInsetProperties))
						{
							if (key.endsWith('image') || key.endsWith('url') || key.endsWith('height') || key.endsWith('anchor'))
							{
								visibleProperties[key] = currentInsetProperties[key];
							}
						}
						break;
					case 'step':
						for (let key of Object.keys(currentInsetProperties))
						{
							if (key.endsWith('image') || key.endsWith('step') || key.endsWith('height') || key.endsWith('anchor'))
							{
								visibleProperties[key] = currentInsetProperties[key];
							}
						}
						break;
					default:
						break;
				}
			}
		}
		visibleProperties.visible = props.properties.visible;
	}
	//#endregion

	if(props.subcomponent === SubcomponentType.BUTTONS)
	{
		visibleProperties = {};
		let currentButton = null;
		for (let key of Object.keys(props.properties))
		{
			if (key.endsWith('properties') || props.properties[key].type === 'group')
			{
				visibleProperties[key] = props.properties[key];
				let propertyValue = getPropertyValue(key);
				if(propertyValue === null || propertyValue === undefined) propertyValue = getPropertyDefaultValue(key);
				if(propertyValue === true)
				{
					currentButton = key.substring(0, 7);
				}
				visibleProperties[key].className = 'propertyGroupPadLeft';
			}
			if(key.startsWith(currentButton))
			{
				visibleProperties[key] = props.properties[key];
			}
		}
	}

	if (props.subcomponent === SubcomponentType.OPTIONS || props.subcomponent === SubcomponentType.HEADERS)
	{
		visibleProperties = {};
		let currentIndex = null;
		for (let key of Object.keys(props.properties))
		{
			if (key.endsWith('properties') || props.properties[key].type === 'group')
			{
				visibleProperties[key] = props.properties[key];
				let propertyValue = getPropertyValue(key);
				if(propertyValue === null || propertyValue === undefined) propertyValue = getPropertyDefaultValue(key);
				if(propertyValue === true)
				{
					currentIndex = key.split(' ')[1];
				}
			}

			if(key.split(' ')[1] === currentIndex || isNaN(key.split(' ')[1]))
			{
				visibleProperties[key] = props.properties[key];
			}
		}
	}

	if (visibleProperties)
	{
		for (let key of Object.keys(visibleProperties))
		{
			const currentValue = getPropertyValue(key) ? getPropertyValue(key) : '';
			const commonProps = {
				key: key,
				label: key,
				value: currentValue,
				defaultValue: getPropertyDefaultValue(key) ? getPropertyDefaultValue(key) : "",
				onChangeCallback: ((v) => 
				{
					onValueChanged(key, v)
				}),
				onCommitCallback: ((v) => 
				{
					onValueCommitted(key, v)
				}),
				contextMenuAddons: [],
				contextMenuOverride: getPropertyChanged(key) ? [
					{
						renderObject: "Sync to template",
						id: "syncToTemplate",
						callback: () =>
						{
							onValueChanged(key, currentValue, false)
							onValueCommitted(key, currentValue, false);
						},
					},
				] : props.slide ? undefined : []
			};

			switch (visibleProperties[key].type)
			{
				case PropertyTypes.Group:
					properties.push(
						<HeaderProperty data-name={"ui-group-toggle-" + key + "-" + props.title} title={visibleProperties[key].title} className={visibleProperties[key].className} {...commonProps} decorators={visibleProperties[key].decorators} onCommitCallback={undefined} contextMenuOverride={[]}/>
					);
					break;
				case PropertyTypes.Text:
					properties.push(
						<StringProperty data-name={"ui-template-text-" + key + "-" + props.title } {...commonProps} allowTextVariables={true} />
					);
					break;
				case PropertyTypes.TextArea:
					properties.push(
						<TextProperty data-name={"ui-template-textarea-" + key + "-" + props.title } {...commonProps} allowTextVariables={true} />
					);
					break;
				case PropertyTypes.Numeric:
					properties.push(
						<NumberProperty data-name={"ui-template-number-" + key + "-" + props.title } {...Object.assign({}, commonProps, {
							value: getPropertyValue(key) != undefined ? getPropertyValue(key) : 0,
							step: visibleProperties[key].step ?? 0.1,
						})} />
					);
					break;
				case PropertyTypes.Toggle:
					properties.push(
						<BoolProperty data-name={"ui-template-toggle-" + key + "-" + props.title } {...commonProps} onChangeCallback={undefined}/>
					);
					break;
				case PropertyTypes.Image:
					properties.push(
						<AssetProperty data-name={"ui-template-image-" + key + "-" + props.title } {...commonProps} onChangeCallback={undefined} type="image" />
					);
					break;
				case PropertyTypes.Video:
					properties.push(
						<AssetProperty data-name={"ui-template-video-" + key + "-" + props.title } {...commonProps} onChangeCallback={undefined} type="video" />
					);
					break;
				case PropertyTypes.Audio:
					properties.push(
						<AssetProperty data-name={"ui-template-audio-" + key + "-" + props.title } {...commonProps} onChangeCallback={undefined} type="audio" />
					);
					break;
				case PropertyTypes.Color:
				//color
					let alphaProperty = findAlpha(visibleProperties, key);
					if(!alphaProperty)
					{
						properties.push(
							<ColorProperty data-name={"ui-template-color-" + key + "-" + props.title } {...Object.assign({}, commonProps, {
								value: getPropertyValue(key) ? getPropertyValue(key) : visibleProperties[key].default ? visibleProperties[key].default : '#ffffff',
							})} />
						);
					}
					else 
					{
					//push combined color and alpha here
						properties.push(
							[
								<ColorAlphaProperty data-name={"ui-template-color-" + key + "-" + props.title }
									key={key}
									name={key}
									label={key}
									valueColor={getPropertyValue(key) ? getPropertyValue(key) : visibleProperties[key].default ? visibleProperties[key].default : '#ffffff'}
									valueAlpha={getPropertyValue(alphaProperty.key)!=undefined ? getPropertyValue(alphaProperty.key) : visibleProperties[alphaProperty.key].default ? visibleProperties[alphaProperty.key].default : 0}
									postfixLabel={'%'}
									defaultValueColor={getPropertyDefaultValue(key) ? getPropertyDefaultValue(key) : ""}
									defaultValueAlpha={getPropertyDefaultValue(alphaProperty.key) ? getPropertyDefaultValue(alphaProperty.key) : ""}
									min={visibleProperties[alphaProperty.key].min ? visibleProperties[alphaProperty.key].min : 0}
									max={visibleProperties[alphaProperty.key].max ? visibleProperties[alphaProperty.key].max : 100}
									step={visibleProperties[alphaProperty.key].step ? visibleProperties[alphaProperty.key].step : 1}
									hideArrows={true}
									onChangeCallbackColor={(v) => onValueChanged(key, v)}
									onCommitCallbackColor={(v) => onValueCommitted(key, v)}
									onChangeCallbackAlpha={(v) => onValueChanged(alphaProperty.key, v)}
									onCommitCallbackAlpha={(v) => onValueCommitted(alphaProperty.key, v)}
									contextMenuAddonsColor={[]}
									contextMenuOverrideColor={getPropertyChanged(key) ? [
										{
											renderObject: "Sync to template",
											id: "syncToTemplate",
											callback: () =>
											{
												onValueChanged(key, currentValue, false)
												onValueCommitted(key, currentValue, false);
											},
										},
									] : props.slide ? undefined : []}
									contextMenuAddonsAlpha={[]}
									contextMenuOverrideAlpha={getPropertyChanged(alphaProperty.key) ? [
										{
											renderObject: "Sync to template",
											id: "syncToTemplate",
											callback: () =>
											{
												onValueChanged(alphaProperty.key, currentValue, false)
												onValueCommitted(alphaProperty.key, currentValue, false);
											},
										},
									] : props.slide ? undefined : []}
								/>,
							]
						);
					}
					break;
				case PropertyTypes.Range:
				//alpha
					if(key.endsWith('alpha')) break; //we won't draw alpha seperately anymore
					properties.push(
						<SliderProperty data-name={"ui-template-range-" + key + "-" + props.title } {...commonProps}
							min={visibleProperties[key].min ? visibleProperties[key].min : 0}
							max={visibleProperties[key].max ? visibleProperties[key].max : 100}
							step={visibleProperties[key].step ? visibleProperties[key].step : 0.01}
							value={getPropertyValue(key)!=undefined ? getPropertyValue(key) : visibleProperties[key].default ? visibleProperties[key].default : 0}
							onChangeCallback={(v) => onValueChanged(key, v)}
							onCommitCallback={(v) => onValueCommitted(key, v)}
						/>
					);
					break;
				case PropertyTypes.Step:
					properties.push(
						<StepProperty data-name={"ui-template-step-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : '#ffffff'}
							onChangeCallback={undefined}
							onCommitCallback={(v) => onValueCommitted(key, v)}
						/>
					);
					break;
				case PropertyTypes.Url:
					properties.push(
						<StringProperty data-name={"ui-template-url-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : ''}
							onChangeCallback={(v) => onValueChanged(key, v)}
							onCommitCallback={(v) => onValueCommitted(key, v)}
						/>
					);
					break;
				case PropertyTypes.Camera:
					properties.push(
						<CameraProperty data-name={"ui-template-camera-" + key + "-" + props.title } {...commonProps} onChangeCallback={undefined} contextMenuOverride={[]}/>
					);
					break;
				case PropertyTypes.Anchor:
					properties.push(
						<IconSelectionProperty data-name={"ui-template-anchor-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : 'top'}
							options={[
								{ display: <AnchorTopIcon/>, value: "top" },
								{ display: <AnchorMiddleIcon/>, value: "center" },
								{ display: <AnchorBottomIcon/>, value: "bottom" },
							]}
							onChangeCallback={undefined}
						/>
					);
					break;
				case PropertyTypes.InsetAnchor:
					properties.push(
						<IconSelectionProperty data-name={"ui-template-inset-anchor-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : 'top-middle'}
							options={
								[
									[
										{ display: <AlignTextLeftIcon/>, value: "top-left" },
										{ display: <AlignTextCenterIcon/>, value: "top-middle" },
										{ display: <AlignTextRightIcon/>, value: "top-right" },
									],
									[
										{ display: <AlignTextLeftIcon/>, value: "middle-left" },
										{ display: <AlignTextCenterIcon/>, value: "center" },
										{ display: <AlignTextRightIcon/>, value: "middle-right" },
									],
									[
										{ display: <AlignTextLeftIcon/>, value: "bottom-left" },
										{ display: <AlignTextCenterIcon/>, value: "bottom-middle" },
										{ display: <AlignTextRightIcon/>, value: "bottom-right" },
									],
								]}
							onChangeCallback={undefined}
						/>
					);
					break;
				case PropertyTypes.Align:
					properties.push(
						<IconSelectionProperty data-name={"ui-template-anchor-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : 'left'}
							options={[
								{ display: <AlignTextLeftIcon/>, value: "left" },
								{ display: <AlignTextCenterIcon/>, value: "middle" },
								{ display: <AlignTextRightIcon/>, value: "right" },
							]}
							onChangeCallback={undefined}
						/>
					);
					break;
				case PropertyTypes.TextAreaPreset:
					properties.push(
						<DropdownProperty data-name={"ui-template-textarea-preset-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : 'top'}
							options={[
								{ display: "Headers Bottom", value: "headers-bottom" },
								{ display: "EULA", value: "eula" },
								{ display: "Headers Top", value: "headers-top" },
							]}
							onChangeCallback={undefined}
						/>
					);
					break;
				case PropertyTypes.OptionLayout:
					properties.push(
						<DropdownProperty data-name={"ui-template-option-layout-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : 'single column'}
							options={[
								{ display: "1 Column", value: "single column" },
								{ display: "2 Columns", value: "2 columns" },
								{ display: "3 Columns", value: "3 columns" },
							]}
							onChangeCallback={undefined}
						/>
					);
					break;
				case PropertyTypes.Size3:
					properties.push(
						<DropdownProperty data-name={"ui-template-size-3-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : 'small'}
							options={[
								{ display: "Small", value: "small" },
								{ display: "Medium", value: "medium" },
								{ display: "Large", value: "large" },
							]}
							onChangeCallback={undefined}
						/>
					);
					break;
				case PropertyTypes.FontSize:
					properties.push(
						<SliderProperty data-name={"ui-template-font-size-" + key + "-" + props.title} {...commonProps}
							min={12}
							max={48}
							step={2}
							value={getPropertyValue(key) != undefined ? getPropertyValue(key) : 14}
							field={true}
							onChangeCallback={(v) => onValueChanged(key, v)}
							onCommitCallback={(v) => onValueCommitted(key, v)}
							decimals={0}
						/>
					);
					break;
				case PropertyTypes.InsetType:
					properties.push(
						<DropdownProperty data-name={"ui-template-inset-type-" + key + "-" + props.title } {...commonProps}
							value={getPropertyValue(key) ? getPropertyValue(key) : 'invisible'}
							options={[
								{ display: "Invisible", value: "invisible" },
								{ display: "Image", value: "image" },
								{ display: "Step", value: "step" },
								{ display: "Video", value: "video" },
								{ display: "Audio", value: "audio" },
								{ display: "Url", value: "url" },
								{ display: "Camera", value: "camera" },
							]}
							onChangeCallback={undefined}
						/>
					);
					break;
				default:
					console.error("Unknown UI subcomponent type: " + visibleProperties[key].type);
					break;
			}
		}
	}

	return (
		<PropertyGroup title={props.title} minHeight={32}>
			{properties}
		</PropertyGroup>
	)
}